import React, { useMemo, useState } from "react";

import type { GridApi, IRowNode, RowClickedEvent, GetContextMenuItemsParams, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { useQueries, useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";

import { getCountForModelQueryOptions } from "@synthesis/shared-services/viewer/markupController/hooks";
import { getStatus } from "../../../api";
import { Table } from "@synthesis/shared-ui";

import { useWSNotifications } from "../../../../../../app/hooks";

import { StoreState, useFileSystemWorker, useStores } from "../../../store";
import { useAuthorNames, useGetContextMenuItems, useIfcNames } from "../hooks";
import { buildDrawingsColumns, buildTableColumns } from "./table-view.columns";
import {
  buildMarkupsByFileId,
  buildModelStatusesById,
  buildTableRows,
  formatFileSize,
  getAuthorIds,
  getFilesSummary,
  getIfcIds,
  getModelIdsForMarkups,
  getModelIdsForStatus,
  getSourceFiles,
  getTableId,
  getTableViewFlags,
  mergeModelStatusKinds,
  MODELS_STATUS_QUERY_KEY,
  parseRunnerJobStateMessage,
  type FileMetaWithExtras,
  type ModelStatusKind,
  type TableRow,
  type TableViewType,
} from "./table-view.lib";

const TableView = observer(
  ({
    stateFunctions,
    setSelectedFile,
    disabledRight,
    drawingsSearch = "",
    drawings,
    drawingsLoading = false,
    variant = "default",
    pickerFiles = [],
    pickerSearch = "",
    pickerIsLoading = false,
    pickerSelectedIds,
    onPickerSelectionChange,
  }: TableViewType) => {
    const { fileStore, generalStore } = useStores();
    useFileSystemWorker();
    const { clientWebSocket } = useWSNotifications();
    const [socketModelStatuses, setSocketModelStatuses] = useState<Record<string, ModelStatusKind | null>>({});
    const { getContextMenuItemsForFile } = useGetContextMenuItems();
    const [selectedInfo, setSelectedInfo] = useState({
      count: 0,
      size: 0,
    });
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const { isModelsPicker, isDrawingsTab, isDocsTab, isModelsTab } = getTableViewFlags(variant, generalStore.activeTab);

    const drawingsAsyncDataRef = React.useRef({
      drawings: drawings ?? [],
      userNamesById: {} as Record<string, string | null>,
      ifcNamesById: {} as Record<string, string | null>,
    });

    const suppressSelectionSyncRef = React.useRef(isModelsPicker);

    const selectedRows = useMemo(
      () => (isModelsPicker ? pickerSelectedIds : Array.from(generalStore.checkedFiles.keys())),
      [generalStore.checkedFiles, isModelsPicker, pickerSelectedIds],
    );

    const visibleFiles = isModelsPicker ? pickerFiles : fileStore.visibleFiles;

    const sourceFiles = useMemo(() => getSourceFiles(visibleFiles, isModelsPicker, pickerSearch), [isModelsPicker, pickerSearch, visibleFiles]);

    const rawAuthorIds = useMemo(
      () => getAuthorIds({ isDrawingsTab, isDocsTab, drawings, sourceFiles }),
      [isDrawingsTab, isDocsTab, drawings, sourceFiles],
    );

    const { userNamesById } = useAuthorNames(rawAuthorIds);

    const rawIfcIds = useMemo(() => getIfcIds(drawings, isDrawingsTab), [isDrawingsTab, drawings]);

    const { ifcNamesById } = useIfcNames(rawIfcIds);

    drawingsAsyncDataRef.current = {
      drawings: drawings ?? [],
      userNamesById,
      ifcNamesById,
    };

    const modelIdsForMarkups = useMemo(() => getModelIdsForMarkups(sourceFiles, isDrawingsTab, isDocsTab), [sourceFiles, isDrawingsTab, isDocsTab]);

    const modelIdsForStatus = useMemo(() => getModelIdsForStatus(sourceFiles, isModelsTab), [sourceFiles, isModelsTab]);

    const markupQueries = useQueries({
      queries: modelIdsForMarkups.map((modelId) => getCountForModelQueryOptions({ path: { modelId } })),
    });

    const markupsByFileId = useMemo(
      () =>
        buildMarkupsByFileId(
          modelIdsForMarkups,
          markupQueries.map((query) => query.data?.markupCount),
        ),
      [modelIdsForMarkups, markupQueries],
    );

    const modelsStatusQuery = useQuery({
      queryKey: MODELS_STATUS_QUERY_KEY(generalStore.projectId, modelIdsForStatus),
      queryFn: async () => (await getStatus({ body: { modelIds: modelIdsForStatus } })).data,
      enabled: isModelsTab && modelIdsForStatus.length > 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    React.useEffect(() => {
      if (!isModelsTab || !clientWebSocket) {
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (typeof event.data !== "string") {
          return;
        }

        const update = parseRunnerJobStateMessage(event.data, modelIdsForStatus);

        if (!update) {
          return;
        }

        setSocketModelStatuses((prev) => {
          const next = { ...prev };

          for (const modelId of update.modelIds) {
            next[modelId] = update.statusKind;
          }

          return next;
        });
      };

      clientWebSocket.addEventListener("message", handleMessage);

      return () => {
        clientWebSocket.removeEventListener("message", handleMessage);
      };
    }, [isModelsTab, clientWebSocket, modelIdsForStatus]);

    const modelStatusesById = useMemo<Record<string, ModelStatusKind>>(
      () => (isModelsTab ? mergeModelStatusKinds(buildModelStatusesById(modelsStatusQuery.data ?? []), socketModelStatuses) : {}),
      [isModelsTab, modelsStatusQuery.data, socketModelStatuses],
    );

    const tableData = useMemo(
      () =>
        buildTableRows({
          isDrawingsTab,
          drawings: drawings ?? [],
          drawingsSearch,
          sortType: generalStore.sortType,
          sourceFiles,
          markupsByFileId,
          userNamesById,
          ifcNamesById,
        }),
      [isDrawingsTab, drawings, drawingsSearch, generalStore.sortType, sourceFiles, markupsByFileId, userNamesById, ifcNamesById],
    );

    React.useEffect(() => {
      if (!gridApi || tableData.length === 0) return;

      const selectedIds = isModelsPicker ? new Set(pickerSelectedIds ?? []) : null;
      const shouldSelect = (id: string) => (isModelsPicker ? selectedIds!.has(id) : generalStore.checkedFiles.has(id));
      const nodesToSelect: IRowNode<TableRow>[] = [];
      const nodesToDeselect: IRowNode<TableRow>[] = [];

      gridApi.forEachNode((node) => {
        if (!node.data) return;
        const wantSelected = shouldSelect(node.data.id);
        const isSelected = Boolean(node.isSelected());
        if (wantSelected && !isSelected) {
          nodesToSelect.push(node);
        } else if (!wantSelected && isSelected) {
          nodesToDeselect.push(node);
        }
      });

      if (nodesToSelect.length === 0 && nodesToDeselect.length === 0) {
        if (isModelsPicker) {
          suppressSelectionSyncRef.current = false;
        }
        return;
      }

      suppressSelectionSyncRef.current = true;
      try {
        if (nodesToDeselect.length > 0) {
          gridApi.setNodesSelected({ nodes: nodesToDeselect, newValue: false });
        }
        if (nodesToSelect.length > 0) {
          gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
        }
      } finally {
        requestAnimationFrame(() => {
          suppressSelectionSyncRef.current = false;
        });
      }
    }, [gridApi, tableData, generalStore.checkedFiles, isModelsPicker, pickerSelectedIds]);

    const onRowClick = (event: RowClickedEvent<TableRow>) => {
      if (isModelsPicker) {
        const target = event.event?.target;
        if (target instanceof Element && target.closest(".ag-checkbox-input-wrapper, .ag-selection-checkbox, input[type='checkbox']")) {
          return;
        }
        event.node?.setSelected(!event.node.isSelected(), false);
        return;
      }

      if (!event.data?.file) return;

      if (isDrawingsTab) {
        stateFunctions.handleDefineDrawing?.(event.data.file);
        return;
      }

      if (generalStore.activeTab === "docs") {
        stateFunctions.openDoc(event.data.file);
        return;
      }

      stateFunctions.openViewer(event.data.file);
    };

    const getContextMenuItems = (params: GetContextMenuItemsParams<TableRow>) => {
      if (isModelsPicker || !params.node?.data?.file) return [];
      const file = params.node.data.file;
      setSelectedFile(file);

      const isClickedSelected = generalStore.checkedFiles.has(file.id);
      if (!isClickedSelected) {
        generalStore.setFilteredContents([{ id: file.id, type: file.type, size: file.size }]);
      }

      const isMultiple = generalStore.checkedFiles.has(file.id) && generalStore.checkedFiles.size > 1;
      return getContextMenuItemsForFile(file, isMultiple, disabledRight, stateFunctions);
    };

    const handleCheckedRows = () => {
      if (suppressSelectionSyncRef.current) return;
      if (!gridApi) return;

      const selectedNodes = gridApi.getSelectedNodes();
      const mappingRows = selectedNodes.map((node) => node.data?.file).filter((file): file is FileMetaWithExtras => !!file);
      const summary = getFilesSummary(mappingRows);

      setSelectedInfo((prev) => (prev.count === summary.count && prev.size === summary.size ? prev : summary));

      if (isModelsPicker) {
        onPickerSelectionChange?.(mappingRows);
        return;
      }

      generalStore.setFilteredContents(mappingRows);
    };

    const onGridReady = (params: GridReadyEvent<TableRow>) => {
      setGridApi(params.api);
    };

    React.useEffect(() => {
      if (!isDrawingsTab || !gridApi) {
        return;
      }

      gridApi.refreshCells({ force: true });
    }, [drawings, userNamesById, ifcNamesById, isDrawingsTab, gridApi]);

    const drawingsColumns = useMemo(() => buildDrawingsColumns(drawingsAsyncDataRef), []);

    const tableColumns = useMemo(
      () =>
        buildTableColumns({
          isDrawingsTab,
          isModelsPicker,
          isDocsTab,
          drawingsColumns,
          sourceFiles,
          pdfPreviewsByFileId: generalStore.pdfPreviewsByFileId,
          userNamesById,
          modelStatusesById,
        }),
      [drawingsColumns, generalStore.pdfPreviewsByFileId, userNamesById, sourceFiles, isDrawingsTab, isModelsPicker, isDocsTab, modelStatusesById],
    );

    const tableId = getTableId({ isModelsPicker, isDrawingsTab, isDocsTab });

    return (
      <div className="flex h-full flex-col justify-between">
        <Table
          key={tableId}
          hasFilters={false}
          isSortable={false}
          tableId={tableId}
          rowModelType="clientSide"
          tableColumns={tableColumns}
          rowData={tableData}
          onRowClicked={onRowClick}
          getContextMenuItems={getContextMenuItems}
          selectionType="multiple"
          selectedRows={selectedRows}
          {...(isModelsPicker
            ? {
                rowSelection: {
                  mode: "multiRow" as const,
                  checkboxes: true,
                  headerCheckbox: true,
                  enableClickSelection: false as const,
                },
              }
            : {})}
          onGridReady={onGridReady}
          onSelectionChanged={handleCheckedRows as (event: SelectionChangedEvent) => void}
          module="gge"
          isLoading={
            isModelsPicker
              ? pickerIsLoading
              : isDrawingsTab
                ? drawingsLoading
                : generalStore.state === StoreState.Loading || generalStore.state === StoreState.Initial
          }
          getRowId={(params) => params.data.id}
        />
        {!isModelsPicker && !isDrawingsTab && (
          <div className="mt-2 border-t-2 pl-3 pt-1">
            Файлов: {tableData.length} {selectedInfo.count > 0 && `Выбрано: ${selectedInfo.count} (${formatFileSize(selectedInfo.size)})`}
          </div>
        )}
      </div>
    );
  },
);

export default TableView;
